import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, User, Calendar, Briefcase, Quote, Filter, HeartHandshake, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { reviewsAPI, galleryAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [featuredOnly, setFeaturedOnly] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filter, featuredOnly]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reviewsRes, statsRes, galleryRes] = await Promise.all([
        reviewsAPI.getPublic(featuredOnly, filter === 'all' ? null : filter),
        reviewsAPI.getStats(),
        galleryAPI.getPairs(true, false, 8)
      ]);
      setReviews(reviewsRes.data.reviews || []);
      setStats(statsRes.data);
      setGallery(galleryRes.data || []);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={`star-${i}`} 
        className={`w-5 h-5 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
      />
    ));
  };

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[var(--background-paper)]/80 backdrop-blur-md border-b border-[var(--border-primary)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <HeartHandshake className="w-8 h-8 text-[var(--primary)]" />
              <span className="text-xl font-bold font-['Cormorant_Garamond'] text-[var(--primary)]">NC Help Hub</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl lg:text-5xl font-bold font-['Cormorant_Garamond'] mb-4">
            What Our <span className="text-[var(--primary)]">Customers Say</span>
          </h1>
          <p className="text-lg text-[var(--text-secondary)] max-w-2xl mx-auto">
            Real reviews from families and businesses who trust NC Help Hub for their local service needs.
          </p>
        </motion.div>

        {/* Stats Section */}
        {stats && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid md:grid-cols-4 gap-6 mb-12"
          >
            <div className="card p-6 text-center">
              <div className="flex justify-center mb-2">
                {renderStars(Math.round(stats.average_rating))}
              </div>
              <p className="text-3xl font-bold text-[var(--primary)]">{stats.average_rating}</p>
              <p className="text-sm text-[var(--text-muted)]">Average Rating</p>
            </div>
            <div className="card p-6 text-center">
              <p className="text-3xl font-bold text-[var(--primary)]">{stats.total_reviews}</p>
              <p className="text-sm text-[var(--text-muted)]">Total Reviews</p>
            </div>
            <div className="card p-6 text-center">
              <p className="text-3xl font-bold text-[var(--success)]">{stats.rating_distribution?.[5] || 0}</p>
              <p className="text-sm text-[var(--text-muted)]">5-Star Reviews</p>
            </div>
            <div className="card p-6 text-center">
              <p className="text-3xl font-bold text-[#D4AF37]">
                {Math.round((stats.rating_distribution?.[5] || 0) / (stats.total_reviews || 1) * 100)}%
              </p>
              <p className="text-sm text-[var(--text-muted)]">Satisfaction Rate</p>
            </div>
          </motion.div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-[var(--text-muted)]" />
            <span className="text-sm text-[var(--text-muted)]">Filter:</span>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Services" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Services</SelectItem>
              <SelectItem value="rides">Rides</SelectItem>
              <SelectItem value="courier">Courier</SelectItem>
              <SelectItem value="laundry">Laundry</SelectItem>
              <SelectItem value="errands">Errands</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant={featuredOnly ? "default" : "outline"}
            onClick={() => setFeaturedOnly(!featuredOnly)}
            className={featuredOnly ? "btn-primary" : ""}
          >
            <Star className={`w-4 h-4 mr-2 ${featuredOnly ? 'fill-current' : ''}`} />
            Featured Only
          </Button>
        </div>

        {/* Before/After Gallery */}
        {gallery.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] mb-6">Our Work</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {gallery.slice(0, 4).map((pair) => (
                <div key={pair.id} className="card overflow-hidden">
                  <div className="grid grid-cols-2 gap-1">
                    <div className="aspect-square relative">
                      <img 
                        src={pair.before_photo} 
                        alt="Before" 
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute bottom-1 left-1 text-xs bg-black/60 text-white px-2 py-0.5 rounded">
                        Before
                      </span>
                    </div>
                    <div className="aspect-square relative">
                      <img 
                        src={pair.after_photo} 
                        alt="After" 
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute bottom-1 left-1 text-xs bg-[var(--success)] text-white px-2 py-0.5 rounded">
                        After
                      </span>
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium">{pair.room || 'Cleaning Service'}</p>
                    <p className="text-xs text-[var(--text-muted)]">By {pair.worker_name}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Reviews Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-[var(--text-muted)]">Loading reviews...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="card p-12 text-center">
            <Star className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No Reviews Yet</h3>
            <p className="text-[var(--text-secondary)]">
              Be the first to leave a review after your service!
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="card p-6"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--primary-light)] flex items-center justify-center">
                    <User className="w-6 h-6 text-[var(--primary)]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold">{review.customer_name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      {renderStars(review.rating)}
                    </div>
                  </div>
                  {review.is_featured && (
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  )}
                </div>
                
                {review.comment && (
                  <div className="relative mb-4">
                    <Quote className="absolute -top-2 -left-2 w-6 h-6 text-[var(--primary-light)]" />
                    <p className="text-[var(--text-secondary)] pl-4">
                      {review.comment}
                    </p>
                  </div>
                )}
                
                <div className="flex items-center gap-4 text-sm text-[var(--text-muted)] border-t border-[var(--border-primary)] pt-4">
                  <span className="flex items-center gap-1">
                    <Briefcase className="w-4 h-4" />
                    {review.service_type === 'private_concierge' ? 'Private Concierge' : review.service_type === 'social_engagement' ? 'Social Engagement' : 'Cleaning'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(review.created_at).toLocaleDateString()}
                  </span>
                </div>
                
                {review.worker_name && (
                  <p className="text-sm text-[var(--text-muted)] mt-2">
                    Service by: {review.worker_name}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card p-8 mt-12 text-center bg-gradient-to-r from-[var(--primary-light)] to-[var(--background-paper)]"
        >
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] mb-4">
            Ready to Experience the NC Help Hub Difference?
          </h2>
          <p className="text-[var(--text-secondary)] mb-6">
            Join hundreds of satisfied customers who trust us with their homes and loved ones.
          </p>
          <Link to="/register">
            <Button className="btn-primary">
              Get Started Today
            </Button>
          </Link>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="bg-[var(--background-paper)] border-t border-[var(--border-primary)] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[var(--text-muted)]">
            © 2024 NC Help Hub. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ReviewsPage;
